import { useCallback, useEffect, useState } from "react";
import { InstitutionWithDatasets } from "../services/index";
import {
  NexBeSignAuthResponse,
  SignatureResultDto,
} from "../services/omniAuthService";

// 전자서명 처리 완료 데이터
export interface SignatureCompletionData {
  provideInstitutionTransmissionList: Array<{
    provideInstitutionNo: string;
    signatureResult: SignatureResultDto | null;
  }>;
}

interface UseOmniAuthOptions {
  selectedInstitutions?: InstitutionWithDatasets[];
  periodicTransfer?: string;
  endDate?: "ONE_YEAR" | "SIX_MONTH";
  agreementText?: string;
  userName?: string;
  onSuccess?: (data: SignatureCompletionData) => void | Promise<void>;
  onError?: (error: string) => void;
}

interface InstitutionSignatureData {
  provideInstitutionNo: string;
  provideInstitutionName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  datasets: any[];
}

export const useOmniAuth = ({
  onSuccess,
  onError,
  options,
}: {
  onSuccess?: (data: SignatureCompletionData) => void | Promise<void>;
  onError?: (error: string) => void;
  options?: UseOmniAuthOptions;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [institutions, setInstitutions] = useState<InstitutionSignatureData[]>(
    []
  );

  // 기관별 전자서명 데이터 초기화
  const initializeInstitutions = useCallback(() => {
    if (!options?.selectedInstitutions) return;

    // 선택된 기관을 기반으로 institutions 생성
    const institutionList: InstitutionSignatureData[] =
      options.selectedInstitutions.map((institution) => ({
        provideInstitutionNo: institution.provideInstitutionNo,
        provideInstitutionName: institution.provideInstitutionName,
        datasets: institution.datasets,
      }));

    setInstitutions(institutionList);
  }, [options?.selectedInstitutions]);

  // options가 변경될 때마다 기관 목록 초기화
  useEffect(() => {
    initializeInstitutions();
  }, [initializeInstitutions]);

  // 모든 기관의 전자서명 완료 처리
  const handleAllSignaturesComplete = useCallback(
    async (
      results: Array<{
        institutionNo: string;
        institutionName: string;
        authResult: {
          parsedData: NexBeSignAuthResponse;
          txId: string;
          userIp: string;
        };
      }>
    ) => {
      setIsModalOpen(false);
      setIsLoading(true);

      try {
        // 날짜 포맷 함수 (yyyy-MM-dd HH:mm:ss)
        const formatDateTime = (date: Date): string => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          const seconds = String(date.getSeconds()).padStart(2, "0");
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        // 날짜 포맷 함수 (yyyy-MM-dd)
        const formatDate = (date: Date): string => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };

        // 공통 데이터 구성
        const now = new Date();
        const agreementDate = formatDate(now); // yyyy-MM-dd 형식
        const agreementDateTime = formatDateTime(now); // yyyy-MM-dd HH:mm:ss 형식

        // 종료일 계산 (ONE_YEAR, SIX_MONTH -> yyyy-MM-dd 형식으로 변환)
        const calculateEndDate = (
          period?: "ONE_YEAR" | "SIX_MONTH"
        ): string => {
          const endDate = new Date(now);
          if (period === "ONE_YEAR") {
            endDate.setFullYear(endDate.getFullYear() + 1);
          } else if (period === "SIX_MONTH") {
            endDate.setMonth(endDate.getMonth() + 6);
          }
          return formatDate(endDate);
        };

        const transmissionDemandAgreementEndDate = options?.endDate
          ? calculateEndDate(options.endDate)
          : agreementDate;

        const commonData = {
          agreementDate,
          transmissionDemandPurpose:
            "전송 요구를 통한 본인 의료기록 조회 및 분석",
          thirdPartyProvisionAgreementYN: "Y",
          regularTransferYN:
            options?.periodicTransfer === "ONCE_A_WEEK" ? "Y" : "N",
          regularTransferCycle:
            options?.periodicTransfer === "ONCE_A_WEEK" ? "1/w" : null,
          transmissionDemandAgreementDateTime: agreementDateTime,
          transmissionDemandAgreementEndDate,
          holdUtilizationPeriod: "9999-12-31",
          consent: options?.agreementText || "",
          userIp: results.length > 0 ? results[0].authResult.userIp : "",
        };

        // 선택된 기관 목록에서 provideInstitutionNo 추출
        const selectedInstitutionNos =
          options?.selectedInstitutions?.map(
            (inst) => inst.provideInstitutionNo
          ) || [];

        // 기관별 전자서명 결과 생성 (selectedInstitutions 기반)
        const provideInstitutionTransmissionList = selectedInstitutionNos.map(
          (institutionNo, index) => {
            // 해당 기관의 전자서명 결과 찾기
            const signatureResult = results.find(
              (r) => r.institutionNo === institutionNo
            );

            // 해당 기관의 정보 가져오기
            const institution = options?.selectedInstitutions?.[index];

            // 해당 기관의 데이터셋 코드 목록 (selectedInstitutions에서 직접 가져옴)
            const agreedDatasets: string[] = institution?.datasets
              ? institution.datasets.map((ds) => ds.datasetCode)
              : [];

            // 동의한 데이터셋이 있고 전자서명도 있는 경우에만 signatureResult 생성
            let signatureResultDto: SignatureResultDto | null = null;

            if (agreedDatasets.length > 0 && signatureResult) {
              signatureResultDto = {
                txId: signatureResult.authResult.parsedData.site_tx_id,
                agreementDate: commonData.agreementDate,
                transmissionDemandPurpose: commonData.transmissionDemandPurpose,
                thirdPartyProvisionAgreementYN:
                  commonData.thirdPartyProvisionAgreementYN,
                regularTransferYN: commonData.regularTransferYN,
                regularTransferCycle: commonData.regularTransferCycle,
                transmissionDemandAgreementDateTime:
                  commonData.transmissionDemandAgreementDateTime,
                transmissionDemandAgreementEndDate:
                  commonData.transmissionDemandAgreementEndDate,
                holdUtilizationPeriod: commonData.holdUtilizationPeriod,
                provideInstitutionNo: institutionNo,
                datasetCodeList: agreedDatasets,
                consent: commonData.consent,
                siteTxId: signatureResult.authResult.parsedData.tx_id,
                caCode: signatureResult.authResult.parsedData.ca_code,
                contentId:
                  signatureResult.authResult.parsedData.signed_content_list[0]
                    ?.content_id || null,
                signedContent:
                  signatureResult.authResult.parsedData.signed_content_list[0]
                    ?.signed_content || "",
                userIp: commonData.userIp,
              };
            }

            return {
              provideInstitutionNo: institutionNo,
              signatureResult: signatureResultDto, // 동의한 데이터셋이 없거나 전자서명이 없으면 null
            };
          }
        );

        // 전자서명 완료 데이터 구성
        const completionData: SignatureCompletionData = {
          provideInstitutionTransmissionList,
        };

        // onSuccess 콜백 호출 (각 페이지에서 API 호출 처리)
        if (onSuccess) {
          await onSuccess(completionData);
        }
      } catch (error) {
        console.error("전자서명 처리 실패:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "전자서명 처리 중 오류가 발생했습니다.";

        if (onError) {
          onError(errorMessage);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess, onError, options]
  );

  // 전자서명 모달 열기 또는 기관 없는 경우 바로 처리
  const openAuth = useCallback(() => {
    if (institutions.length === 0) {
      // 기관이 없는 경우 (기관 없는 데이터셋만 있는 경우)
      // 전자서명 없이 바로 전송
      handleAllSignaturesComplete([]);
      return;
    }

    setIsModalOpen(true);
  }, [institutions, handleAllSignaturesComplete]);

  // 전자서명 모달 닫기
  const closeAuth = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return {
    isModalOpen,
    isLoading,
    institutions,
    totalSteps: institutions.length,
    openAuth,
    closeAuth,
    handleAllSignaturesComplete,
  };
};
